import React, { useEffect, useMemo, useState } from "react";
import Header from "../general/Header";
import Aside from "../general/Aside";
import Footer from "../general/Footer";
import Loading from "../general/Loading";
import Unauthorized from "../general/Unauthorized";
import axios from '../../axios/axiosconf';
import { Link } from "react-router-dom";
import DataTable from "react-data-table-component";
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { getFormaPagoTexto } from '../../../utils/formasPago';
import { useClientPagination } from "./useClientPagination";
import ReportPaginationFooter from "./ReportPaginationFooter";

function EstadoCuentaReportes() {
    const [loggedIn, setLoggedIn] = useState(false);
    const [hasPermission, setHasPermission] = useState(false);
    const [client, setClient] = useState([]);
    const [items, setItems] = useState([]);
    const [facturas, setFacturas] = useState([]);
    const [presupuestos, setPresupuestos] = useState([]);
    const [loadingDetalle, setLoadingDetalle] = useState(false);
    const [filtroTexto, setFiltroTexto] = useState('');
    const [tipoRegistro, setTipoRegistro] = useState('todos');
    const [filtroSaldo, setFiltroSaldo] = useState('pendientes');
    const [fechaInicio, setFechaInicio] = useState('');
    const [fechaFin, setFechaFin] = useState('');
    const [fechaInicioInput, setFechaInicioInput] = useState('');
    const [fechaFinInput, setFechaFinInput] = useState('');
    const [pacienteSeleccionado, setPacienteSeleccionado] = useState(null);

    useEffect(() => {
        if (!localStorage.getItem('userdata')) {
            setHasPermission(false);
            setLoggedIn(true);
            return;
        }

        const userdata = JSON.parse(localStorage.getItem('userdata'));
        const tienePermiso = (userdata.permissions && Array.isArray(userdata.permissions))
            ? userdata.permissions.some(p => p.slug === 'verInfoPacientes' || p.slug === 'verReportes')
            : (userdata.up && (userdata.up.includes('verInfoPacientes') || userdata.up.includes('verReportes')));

        if (!tienePermiso) {
            setHasPermission(false);
            setLoggedIn(true);
            return;
        }

        setHasPermission(true);

        Promise.all([
            axios.get('/get-client?uid=' + userdata.user_id),
            axios.get('/pacientes', {
                params: {
                    uid: userdata.user_id,
                    con_saldo_presupuesto: 1,
                },
            }),
        ])
            .then(([clienteRes, pacientesRes]) => {
                if (clienteRes.status === 200) setClient(clienteRes.data);
                if (pacientesRes.status === 200) setItems(Array.isArray(pacientesRes.data) ? pacientesRes.data : []);
            })
            .catch((error) => {
                console.log(error);
            })
            .finally(() => {
                setLoggedIn(true);
            });
    }, []);

    const getNombreCompleto = (item) => {
        const partes = [
            item?.primer_nombre,
            item?.segundo_nombre,
            item?.primer_apellido,
            item?.segundo_apellido,
        ].filter(Boolean);
        return partes.join(' ').trim() || item?.nombre_paciente || 'Sin nombre';
    };

    const getSaldoPendiente = (row) => parseFloat(row?.total_saldo_disponible || 0);
    const formatearMoneda = (valor) => `$${Math.round(parseFloat(valor || 0)).toLocaleString('es-CO')}`;

    const formatearFecha = (valor) => {
        if (!valor) return '';
        const fecha = new Date(valor);
        if (Number.isNaN(fecha.getTime())) return '';
        return fecha.toLocaleDateString('es-CO');
    };

    const formatearFechaIso = (valor) => {
        if (!valor) return '';
        const fecha = new Date(`${valor}T00:00:00`);
        if (Number.isNaN(fecha.getTime())) return '';
        return fecha.toLocaleDateString('es-CO');
    };

    const tipoEtiqueta = (item) => {
        const tipo = Number(item?.tipo_cliente);
        if (tipo === 1) return 'Empresa';
        return 'Paciente';
    };

    const esFacturaNoElectronica = (factura) =>
        factura?.tipo_factura === 'no_electronica' || [4, 5].includes(parseInt(factura?.estado, 10));

    const getTipoFacturaTexto = (factura) => (esFacturaNoElectronica(factura) ? 'No Electrónica' : 'Electrónica');

    const getEstadoFacturaTexto = (estado) => (parseInt(estado, 10) === 3 ? 'Anulada' : 'Generada');

    const esFacturaAnulada = (factura) => parseInt(factura?.estado, 10) === 3;

    const getEstadoFacturaBadgeStyle = (estado) => {
        switch (parseInt(estado, 10)) {
            case 3:
                return { background: 'rgba(220, 53, 69, 0.08)', color: '#dc3545', border: '1px solid rgba(220, 53, 69, 0.22)', borderRadius: '10px' };
            default:
                return { background: 'rgba(40, 167, 69, 0.08)', color: '#28a745', border: '1px solid rgba(40, 167, 69, 0.22)', borderRadius: '10px' };
        }
    };

    const getNumeroFactura = (factura) => (
        factura?.prefijo && factura?.numero_factura
            ? `${factura.prefijo}-${factura.numero_factura}`
            : (factura?.numero_factura || `#${factura?.id}`)
    );

    const esFacturaBorrador = (factura) => {
        const est = parseInt(factura?.estado, 10);
        return est === 0 || est === 7;
    };

    const getFacturasDePresupuesto = (presupuestoId) => {
        const id = parseInt(presupuestoId, 10);
        return facturas.filter((f) => parseInt(f.presupuesto_id, 10) === id && !esFacturaBorrador(f));
    };

    const filaFacturaPdf = (factura) => [
        getNumeroFactura(factura),
        formatearFecha(factura.fecha_emision),
        factura.concepto_factura_nombre || '—',
        factura.forma_pago && factura.forma_pago !== 'PENDIENTE'
            ? getFormaPagoTexto(factura.forma_pago)
            : '—',
        getTipoFacturaTexto(factura),
        formatearMoneda(factura.valor_total),
        getEstadoFacturaTexto(factura.estado),
    ];

    const cargarDetalle = async (item) => {
        const userdata = JSON.parse(localStorage.getItem('userdata') || '{}');
        if (!item?.id || !userdata?.user_id) return;
        setLoadingDetalle(true);
        setPacienteSeleccionado(item);
        try {
            const [factRes, presRes] = await Promise.all([
                axios.get(`/facturas/paciente/${item.id}`),
                axios.get(`/presupuestos/paciente/${item.id}?uid=${userdata.user_id}`),
            ]);
            setFacturas(Array.isArray(factRes.data) ? factRes.data : []);
            setPresupuestos(Array.isArray(presRes.data) ? presRes.data : []);
        } catch (error) {
            console.log(error);
            setFacturas([]);
            setPresupuestos([]);
        } finally {
            setLoadingDetalle(false);
        }
    };

    const cumpleRango = (fechaValor) => {
        if (!fechaValor) return false;
        const fecha = new Date(fechaValor);
        if (Number.isNaN(fecha.getTime())) return false;

        if (fechaInicio) {
            const inicio = new Date(`${fechaInicio}T00:00:00`);
            if (fecha < inicio) return false;
        }
        if (fechaFin) {
            const fin = new Date(`${fechaFin}T23:59:59`);
            if (fecha > fin) return false;
        }
        return true;
    };

    const facturasFiltradas = useMemo(() => {
        const base = facturas.filter((f) => !esFacturaBorrador(f));
        if (!fechaInicio && !fechaFin) return base;
        return base.filter((f) => cumpleRango(f.fecha_emision || f.created_at));
    }, [facturas, fechaInicio, fechaFin]);

    /** Activo (1) y Pagado (3); se excluyen Generado (0) y Anulado (2). */
    const presupuestosParaReporte = useMemo(() => {
        return presupuestos.filter((p) => [1, 3].includes(Number(p.estado)));
    }, [presupuestos]);

    const presupuestosFiltrados = useMemo(() => {
        if (!fechaInicio && !fechaFin) return presupuestosParaReporte;
        return presupuestosParaReporte.filter((p) => cumpleRango(p.fecha_creacion || p.created_at));
    }, [presupuestosParaReporte, fechaInicio, fechaFin]);

    const saldoFacturas = useMemo(() => {
        return facturasFiltradas.reduce((acc, factura) => {
            const pendiente = (factura.estado === 2 || factura.estado === 5)
                ? parseFloat(factura.valor_pendiente || factura.valor_total || 0)
                : 0;
            return acc + pendiente;
        }, 0);
    }, [facturasFiltradas]);

    const saldoPresupuestos = useMemo(() => {
        return presupuestosFiltrados.reduce((acc, pres) => {
            const esActivo = Number(pres.estado) === 1;
            const saldo = esActivo ? parseFloat(pres.saldo_disponible || 0) : 0;
            return acc + saldo;
        }, 0);
    }, [presupuestosFiltrados]);

    const saldoPendiente = useMemo(() => saldoFacturas + saldoPresupuestos, [saldoFacturas, saldoPresupuestos]);

    const itemsFiltrados = useMemo(() => {
        const texto = filtroTexto.trim().toLowerCase();
        return items.filter((item) => {
            const nombre = getNombreCompleto(item).toLowerCase();
            const doc = String(item?.numero_documento || '').toLowerCase();
            const cel = String(item?.celular || '').toLowerCase();
            const em = String(item?.email || '').toLowerCase();
            const coincideTexto = !texto || nombre.includes(texto) || doc.includes(texto) || cel.includes(texto) || em.includes(texto);
            const esEmpresa = Number(item?.tipo_cliente) === 1;
            const coincideTipo = tipoRegistro === 'todos'
                || (tipoRegistro === 'pacientes' && !esEmpresa)
                || (tipoRegistro === 'empresas' && esEmpresa);
            const saldo = getSaldoPendiente(item);
            const coincideSaldo = filtroSaldo === 'todos'
                || (filtroSaldo === 'pendientes' && saldo > 0)
                || (filtroSaldo === 'al_dia' && saldo <= 0);
            return coincideTexto && coincideTipo && coincideSaldo;
        });
    }, [items, filtroTexto, tipoRegistro, filtroSaldo]);

    const pagination = useClientPagination(itemsFiltrados);

    const customTableStyles = useMemo(() => ({
        table: { style: { backgroundColor: '#fff' } },
        headRow: {
            style: {
                minHeight: '44px',
                borderBottom: '1px solid #e9eef3',
                backgroundColor: '#fafcfe'
            }
        },
        headCells: {
            style: {
                fontSize: '12px',
                fontWeight: 700,
                color: '#5f6b77',
                textTransform: 'uppercase',
                letterSpacing: '0.02em',
                paddingTop: '10px',
                paddingBottom: '10px'
            }
        },
        rows: {
            style: {
                minHeight: '52px',
                borderBottom: '1px solid #f1f4f8'
            }
        },
        cells: {
            style: {
                fontSize: '13px',
                color: '#2f3a45'
            }
        },
    }), []);

    const colsPacientes = useMemo(() => [
        {
            name: 'Tipo',
            selector: (row) => tipoEtiqueta(row),
            width: '110px',
        },
        {
            name: 'Nombre',
            selector: (row) => getNombreCompleto(row),
            grow: 1.5,
            cell: (row) => (
                <span className="patient-name-cell" title={getNombreCompleto(row)}>
                    {getNombreCompleto(row)}
                </span>
            ),
        },
        { name: 'Documento', selector: (row) => row.numero_documento || 'N/A', grow: 0.9 },
        { name: 'Celular', selector: (row) => row.celular || row.telefono || 'N/A', grow: 0.8 },
        { name: 'Email', selector: (row) => row.email || 'N/A', grow: 1 },
        {
            name: 'Saldo pendiente',
            selector: (row) => getSaldoPendiente(row),
            right: true,
            grow: 0.9,
            cell: (row) => {
                const saldo = getSaldoPendiente(row);
                return (
                    <span style={{ fontWeight: 700, color: saldo > 0 ? '#dc3545' : '#28a745', whiteSpace: 'nowrap' }}>
                        {formatearMoneda(saldo)}
                    </span>
                );
            },
        },
        {
            name: 'Acción',
            center: true,
            width: '100px',
            cell: (row) => (
                <button
                    type="button"
                    className="btn btn-info btn-sm"
                    onClick={() => cargarDetalle(row)}
                    title="Estado de cuenta"
                    aria-label="Estado de cuenta"
                >
                    <i className="fas fa-file-invoice-dollar"></i>
                </button>
            ),
        },
    ], []);

    const tituloRango = useMemo(() => {
        if (fechaInicio && fechaFin) {
            return `ESTADO DE CUENTA DE FECHA ${formatearFechaIso(fechaInicio)} A ${formatearFechaIso(fechaFin)}`;
        }
        if (fechaInicio) {
            return `ESTADO DE CUENTA DESDE ${formatearFechaIso(fechaInicio)}`;
        }
        if (fechaFin) {
            return `ESTADO DE CUENTA HASTA ${formatearFechaIso(fechaFin)}`;
        }
        return 'ESTADO DE CUENTA';
    }, [fechaInicio, fechaFin]);

    const exportarPdf = async () => {
        if (!pacienteSeleccionado) return;
        try {
            const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
            const pageWidth = doc.internal.pageSize.getWidth();
            const pageHeight = doc.internal.pageSize.getHeight();
            const margen = 14;
            const ancho = pageWidth - margen * 2;
            let y = 12;
            const azul = [23, 162, 184];
            const grisHeader = [234, 234, 234];
            const tealClaro = [209, 236, 241];
            const nombreEmpresa = client?.nombre || client?.nombre_comercial || 'SmileWEB';

            const asegurarEspacio = (needed = 18) => {
                if (y + needed > pageHeight - 18) {
                    doc.addPage();
                    y = 16;
                }
            };

            const tituloSeccion = (texto) => {
                asegurarEspacio(12);
                doc.setFont('helvetica', 'bold');
                doc.setFontSize(12);
                doc.setTextColor(33, 37, 41);
                doc.text(texto, margen, y);
                y += 4;
            };

            const estilosTabla = {
                theme: 'grid',
                styles: {
                    halign: 'center',
                    fontSize: 8.5,
                    cellPadding: 2,
                    lineColor: [220, 224, 229],
                    lineWidth: 0.2,
                    textColor: [33, 37, 41],
                },
                headStyles: {
                    fillColor: grisHeader,
                    textColor: [31, 41, 55],
                    fontStyle: 'bold',
                    lineColor: [220, 224, 229],
                },
            };

            const aplicarAnuladaParse = (lista) => (data) => {
                if (data.section !== 'body') return;
                const factura = lista[data.row.index];
                if (factura && esFacturaAnulada(factura)) {
                    data.cell.styles.fillColor = [255, 246, 246];
                }
            };

            const aplicarAnuladaDraw = (lista) => (data) => {
                if (data.section !== 'body' || data.column.index !== 0) return;
                const factura = lista[data.row.index];
                if (factura && esFacturaAnulada(factura)) {
                    doc.setDrawColor(220, 53, 69);
                    doc.setLineWidth(1.15);
                    doc.line(data.cell.x + 0.35, data.cell.y, data.cell.x + 0.35, data.cell.y + data.cell.height);
                }
            };

            const piePagina = () => {
                const total = doc.getNumberOfPages();
                for (let i = 1; i <= total; i += 1) {
                    doc.setPage(i);
                    doc.setDrawColor(210, 214, 220);
                    doc.setLineWidth(0.3);
                    doc.line(margen, pageHeight - 12, pageWidth - margen, pageHeight - 12);
                    doc.setFont('helvetica', 'normal');
                    doc.setFontSize(8);
                    doc.setTextColor(100, 116, 139);
                    doc.text(String(i), pageWidth / 2, pageHeight - 7, { align: 'center' });
                    doc.text(`Generado por ${nombreEmpresa}`, pageWidth - margen, pageHeight - 7, { align: 'right' });
                }
            };

            doc.setFillColor(...azul);
            doc.rect(margen, y, ancho, 10, 'F');
            doc.setTextColor(255, 255, 255);
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(13);
            doc.text('ESTADO DE CUENTA', pageWidth / 2, y + 6.8, { align: 'center' });
            y += 15;

            doc.setTextColor(33, 37, 41);
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(9);
            doc.text(`Fecha de emisión: ${formatearFecha(new Date())}`, pageWidth - margen, y, { align: 'right' });
            y += 6;

            const cajaH = 28;
            doc.setDrawColor(209, 213, 219);
            doc.setLineWidth(0.4);
            doc.roundedRect(margen, y, ancho, cajaH, 2.5, 2.5, 'S');
            doc.setDrawColor(220, 224, 229);
            doc.line(margen + ancho / 2, y + 3, margen + ancho / 2, y + cajaH - 3);

            const col1 = margen + 4;
            const col2 = margen + ancho / 2 + 4;
            doc.setFontSize(9);
            const lineaDato = (x, yy, label, valor) => {
                doc.setFont('helvetica', 'bold');
                doc.text(`${label}:`, x, yy);
                const lw = doc.getTextWidth(`${label}: `);
                doc.setFont('helvetica', 'normal');
                doc.text(String(valor || 'N/A'), x + lw + 1, yy);
            };
            lineaDato(col1, y + 7, 'Paciente', getNombreCompleto(pacienteSeleccionado));
            lineaDato(col1, y + 14, 'Teléfono', pacienteSeleccionado?.telefono || 'N/A');
            lineaDato(col1, y + 21, 'Email', pacienteSeleccionado?.email || 'N/A');
            lineaDato(col2, y + 7, 'Documento', pacienteSeleccionado?.numero_documento || 'N/A');
            lineaDato(col2, y + 14, 'Celular', pacienteSeleccionado?.celular || pacienteSeleccionado?.telefono || 'N/A');
            y += cajaH + 8;

            if (fechaInicio || fechaFin) {
                doc.setFont('helvetica', 'italic');
                doc.setFontSize(8.5);
                doc.setTextColor(100, 116, 139);
                doc.text(tituloRango, margen, y);
                doc.setTextColor(33, 37, 41);
                y += 6;
            }

            asegurarEspacio(14);
            doc.setFillColor(209, 236, 241);
            doc.roundedRect(margen, y, ancho, 9, 4.5, 4.5, 'F');
            doc.setTextColor(12, 84, 96);
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(11);
            doc.text(
                `Saldo Pendiente por Cancelar: ${formatearMoneda(saldoPendiente)}`,
                pageWidth / 2,
                y + 6.1,
                { align: 'center' }
            );
            doc.setTextColor(33, 37, 41);
            y += 16;

            tituloSeccion('PRESUPUESTOS ACTIVOS');

            if (!presupuestosFiltrados.length) {
                autoTable(doc, {
                    startY: y,
                    margin: { left: margen, right: margen },
                    ...estilosTabla,
                    headStyles: { ...estilosTabla.headStyles, fillColor: tealClaro },
                    head: [['N° Presupuesto', 'Fecha', 'Pagado', 'Total', 'Pendiente']],
                    body: [['-', '-', '-', '-', '-']],
                    styles: { ...estilosTabla.styles, fontSize: 9.5 },
                });
                y = (doc.lastAutoTable?.finalY || y) + 8;
            } else {
                presupuestosFiltrados.forEach((pres) => {
                    const total = parseFloat(pres.valor_total || 0);
                    const pagado = parseFloat(pres.valor_consumido || 0);
                    const pendiente = parseFloat(pres.saldo_disponible || 0);
                    const numeroPres = pres.numero_presupuesto || `#${pres.id}`;
                    const facturasAsociadas = getFacturasDePresupuesto(pres.id);

                    asegurarEspacio(28);
                    autoTable(doc, {
                        startY: y,
                        margin: { left: margen, right: margen },
                        ...estilosTabla,
                        headStyles: { ...estilosTabla.headStyles, fillColor: tealClaro },
                        head: [['N° Presupuesto', 'Fecha', 'Pagado', 'Total', 'Pendiente']],
                        body: [[
                            numeroPres,
                            formatearFecha(pres.fecha_creacion),
                            formatearMoneda(pagado),
                            formatearMoneda(total),
                            formatearMoneda(pendiente),
                        ]],
                        styles: { ...estilosTabla.styles, fontSize: 9.5 },
                    });

                    y = (doc.lastAutoTable?.finalY || y) + 2;
                    autoTable(doc, {
                        startY: y,
                        margin: { left: margen, right: margen },
                        theme: 'grid',
                        head: facturasAsociadas.length
                            ? [
                                [{ content: `Facturas asociadas a ${numeroPres}`, colSpan: 7, styles: { halign: 'left' } }],
                                ['N° Factura', 'Fecha', 'Concepto', 'Forma de pago', 'Tipo', 'Total', 'Estado'],
                            ]
                            : [[{ content: `Facturas asociadas a ${numeroPres}`, colSpan: 7, styles: { halign: 'left' } }]],
                        body: facturasAsociadas.length
                            ? facturasAsociadas.map(filaFacturaPdf)
                            : [[{ content: 'Sin facturas asociadas', colSpan: 7, styles: { halign: 'left', textColor: [100, 116, 139], fontStyle: 'italic' } }]],
                        styles: { ...estilosTabla.styles, fontSize: 8 },
                        headStyles: { fillColor: grisHeader, textColor: [31, 41, 55], fontStyle: 'bold', lineColor: [220, 224, 229] },
                        didParseCell: (data) => {
                            if (data.section === 'head' && data.row.index === 1) {
                                data.cell.styles.fillColor = [222, 222, 222];
                                data.cell.styles.textColor = [51, 65, 85];
                            }
                            if (facturasAsociadas.length) {
                                aplicarAnuladaParse(facturasAsociadas)(data);
                            }
                        },
                        didDrawCell: facturasAsociadas.length ? aplicarAnuladaDraw(facturasAsociadas) : undefined,
                    });
                    y = (doc.lastAutoTable?.finalY || y) + 8;
                });
            }

            tituloSeccion('LISTADO GENERAL DE FACTURAS');
            autoTable(doc, {
                startY: y,
                margin: { left: margen, right: margen },
                ...estilosTabla,
                head: [['N° Factura', 'Fecha', 'Concepto', 'Forma de pago', 'Tipo', 'Total', 'Estado']],
                body: facturasFiltradas.length
                    ? facturasFiltradas.map(filaFacturaPdf)
                    : [['-', '-', '-', '-', '-', '-', '-']],
                didParseCell: facturasFiltradas.length ? aplicarAnuladaParse(facturasFiltradas) : undefined,
                didDrawCell: facturasFiltradas.length ? aplicarAnuladaDraw(facturasFiltradas) : undefined,
            });

            piePagina();
            doc.save(`estado-cuenta-${pacienteSeleccionado.id}.pdf`);
        } catch (error) {
            console.log(error);
            alert('No fue posible exportar el PDF');
        }
    };

    if (!loggedIn) return <Loading />;
    if (!hasPermission) return <Unauthorized />;

    return (
        <div className="wrapper hold-transition" style={{ fontSize: '13px' }}>
            <Header client={client} />
            <Aside client={client} activelink="reports" />

            <div className="content-wrapper">
                <div className="content-header">
                    <div className="container-fluid">
                        <div className="row mb-2">
                            <div className="col-sm-12">
                                <ol className="breadcrumb float-sm-left mb-0">
                                    <li className="breadcrumb-item"><Link to="/home">Home</Link></li>
                                    <li className="breadcrumb-item"><Link to="/estadisticas">Reportes</Link></li>
                                    <li className="breadcrumb-item"><Link to="/estadisticas-contables">Contables</Link></li>
                                    <li className="breadcrumb-item active">Estado de cuenta</li>
                                </ol>
                            </div>
                        </div>
                    </div>
                </div>

                <section className="content">
                    <div className="container-fluid">
                        {!pacienteSeleccionado && (
                            <div className="patient-list-page">
                                <div className="card">
                                    <div className="card-header d-flex align-items-center">
                                        <Link to="/estadisticas-contables" className="btn btn-outline-secondary btn-sm mr-3" title="Volver">
                                            <i className="fas fa-arrow-left"></i>
                                        </Link>
                                        <h3 className="card-title mb-0"><i className="fas fa-users mr-2"></i>Pacientes y Empresas</h3>
                                    </div>
                                    <div className="card-body">
                                        <div className="patient-filters-box mb-3">
                                            <div className="row">
                                                <div className="col-md-5 mb-2 mb-md-0">
                                                    <div className="form-group patient-filter-group mb-0">
                                                        <label>Buscar</label>
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            placeholder="Nombre, documento, celular o email"
                                                            value={filtroTexto}
                                                            onChange={(e) => setFiltroTexto(e.target.value)}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="col-md-3 mb-2 mb-md-0">
                                                    <div className="form-group patient-filter-group mb-0">
                                                        <label>Tipo</label>
                                                        <select className="form-control" value={tipoRegistro} onChange={(e) => setTipoRegistro(e.target.value)}>
                                                            <option value="todos">Todos</option>
                                                            <option value="pacientes">Solo pacientes</option>
                                                            <option value="empresas">Solo empresas</option>
                                                        </select>
                                                    </div>
                                                </div>
                                                <div className="col-md-4">
                                                    <div className="form-group patient-filter-group mb-0">
                                                        <label>Estado de Cuenta</label>
                                                        <select className="form-control" value={filtroSaldo} onChange={(e) => setFiltroSaldo(e.target.value)}>
                                                            <option value="todos">Todos</option>
                                                            <option value="al_dia">Al día</option>
                                                            <option value="pendientes">Con Saldo Pendiente</option>
                                                        </select>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div style={{ boxShadow: '0 0 14px rgba(0,0,0,0.09)', backgroundColor: 'white', borderRadius: 8 }}>
                                            <DataTable
                                                columns={colsPacientes}
                                                data={pagination.paginatedItems}
                                                customStyles={customTableStyles}
                                                noDataComponent="No hay registros para mostrar."
                                            />
                                            <ReportPaginationFooter
                                                totalRegistros={pagination.totalRegistros}
                                                indicePrimerRegistro={pagination.indicePrimerRegistro}
                                                indiceUltimoRegistro={pagination.indiceUltimoRegistro}
                                                paginaActual={pagination.paginaActual}
                                                cambiarPagina={pagination.cambiarPagina}
                                                totalPaginas={pagination.totalPaginas}
                                                registrosPorPagina={pagination.registrosPorPagina}
                                                onPageSizeChange={pagination.handlePageSizeChange}
                                                itemLabel="registros"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {pacienteSeleccionado && (
                            <div>
                                <div className="card" style={{ borderRadius: '12px', border: '1px solid #e9ecef' }}>
                                    <div className="card-header d-flex align-items-center flex-wrap">
                                        <button
                                            className="btn btn-outline-secondary btn-sm mr-3 mb-2 mb-md-0"
                                            onClick={() => {
                                                setPacienteSeleccionado(null);
                                                setFacturas([]);
                                                setPresupuestos([]);
                                                setFechaInicio('');
                                                setFechaFin('');
                                                setFechaInicioInput('');
                                                setFechaFinInput('');
                                            }}
                                            title="Volver"
                                            aria-label="Volver"
                                        >
                                            <i className="fas fa-arrow-left"></i>
                                        </button>
                                        <h3 className="card-title mb-0">
                                            Estado de cuenta
                                        </h3>
                                    </div>
                                    <div className="card-body">
                                        <style>{`
                                            .ec-report-title {
                                                background: #17a2b8;
                                                color: #fff;
                                                font-weight: 800;
                                                font-size: 1.15rem;
                                                letter-spacing: 0.04em;
                                                text-align: center;
                                                padding: 10px 14px;
                                                border-radius: 8px;
                                                margin-bottom: 8px;
                                            }
                                            .ec-fecha-emision {
                                                text-align: right;
                                                font-size: 0.82rem;
                                                color: #334155;
                                                margin-bottom: 12px;
                                            }
                                            .ec-info-card {
                                                border: 1px solid #d1d5db;
                                                border-radius: 10px;
                                                background: #fff;
                                                overflow: hidden;
                                            }
                                            .ec-info-grid {
                                                display: grid;
                                                grid-template-columns: 1fr 1fr;
                                            }
                                            .ec-info-col {
                                                padding: 14px 16px;
                                            }
                                            .ec-info-col + .ec-info-col {
                                                border-left: 1px solid #e5e7eb;
                                            }
                                            .ec-info-col p {
                                                margin: 0 0 8px;
                                                font-size: 0.88rem;
                                            }
                                            .ec-info-col p:last-child { margin-bottom: 0; }
                                            .ec-saldo-franja {
                                                background: #d1ecf1;
                                                color: #0c5460;
                                                font-weight: 700;
                                                font-size: 1.05rem;
                                                text-align: center;
                                                padding: 10px 18px;
                                                border-radius: 999px;
                                            }
                                            .ec-section-title {
                                                font-weight: 800;
                                                font-size: 0.95rem;
                                                letter-spacing: 0.04em;
                                                color: #1f2937;
                                                margin: 0;
                                                padding: 10px 14px 8px;
                                            }
                                            .ec-note-card { padding: 12px 14px; color: #1e293b; }
                                            .ec-table > thead > tr > th {
                                                background: #eaeaea;
                                                color: #1f2937;
                                                border-color: #e5e7eb;
                                                font-weight: 700;
                                            }
                                            .ec-table-presupuestos > thead > tr > th {
                                                background: #d1ecf1 !important;
                                                color: #0c5460 !important;
                                                border-color: #d1ecf1 !important;
                                            }
                                            .presupuesto-facturas-table thead th,
                                            .presupuesto-facturas-table th {
                                                background: #eaeaea !important;
                                                color: #1f2937 !important;
                                                border-color: #e5e7eb !important;
                                                border-bottom: 1px solid #e5e7eb !important;
                                            }
                                            .ec-table td.concepto-cell {
                                                white-space: nowrap;
                                                max-width: 140px;
                                                overflow: hidden;
                                                text-overflow: ellipsis;
                                            }
                                            .ec-table tr.fila-anulado td {
                                                background: #fff8f8;
                                            }
                                            .ec-table tr.fila-anulado td:first-child {
                                                box-shadow: inset 3px 0 0 #dc3545;
                                            }
                                            .presupuesto-facturas-row td {
                                                background: #fff;
                                                padding: 0 10px 10px 10px !important;
                                                border-top: none !important;
                                            }
                                            .presupuesto-facturas-panel {
                                                background: #fff;
                                                border: 1px solid #d1d5db;
                                                border-radius: 6px;
                                                overflow: hidden;
                                            }
                                            .presupuesto-facturas-panel-title {
                                                font-size: 12px;
                                                font-weight: 700;
                                                color: #1f2937;
                                                margin: 0;
                                                padding: 7px 10px;
                                                background: #eaeaea;
                                            }
                                            .presupuesto-facturas-table {
                                                width: 100%;
                                                margin: 0;
                                                font-size: 12px;
                                                background: #fff;
                                            }
                                            .presupuesto-facturas-table th {
                                                background: #eaeaea;
                                                font-weight: 600;
                                                text-align: center;
                                                padding: 4px 6px;
                                                border-bottom: 1px solid #e5e7eb;
                                            }
                                            .presupuesto-facturas-table td {
                                                text-align: center;
                                                padding: 4px 6px;
                                                border-bottom: 1px solid #f1f5f9;
                                            }
                                            .presupuesto-facturas-table tr.fila-anulado td {
                                                background: #fff8f8;
                                            }
                                            .presupuesto-facturas-table tr.fila-anulado td:first-child {
                                                box-shadow: inset 3px 0 0 #dc3545;
                                            }
                                            @media (max-width: 767.98px) {
                                                .ec-info-grid { grid-template-columns: 1fr; }
                                                .ec-info-col + .ec-info-col { border-left: none; border-top: 1px solid #e5e7eb; }
                                            }
                                        `}</style>
                                        <div className="ec-report-title">ESTADO DE CUENTA</div>
                                        <div className="ec-fecha-emision">Fecha de emisión: {formatearFecha(new Date())}</div>
                                        <div className="ec-info-card mb-3">
                                            <div className="ec-info-grid">
                                                <div className="ec-info-col">
                                                    <p><strong>Paciente:</strong> {getNombreCompleto(pacienteSeleccionado)}</p>
                                                    <p><strong>Teléfono:</strong> {pacienteSeleccionado.telefono || 'N/A'}</p>
                                                    <p><strong>Email:</strong> {pacienteSeleccionado.email || 'N/A'}</p>
                                                </div>
                                                <div className="ec-info-col">
                                                    <p><strong>Documento:</strong> {pacienteSeleccionado.numero_documento || 'N/A'}</p>
                                                    <p><strong>Celular:</strong> {pacienteSeleccionado.celular || pacienteSeleccionado.telefono || 'N/A'}</p>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="mb-3">
                                            <div className="ec-saldo-franja">
                                                Saldo Pendiente por Cancelar: {formatearMoneda(saldoPendiente)}
                                            </div>
                                        </div>

                                        <div className="ec-info-card p-3 mb-3">
                                            <div className="row mb-0">
                                                <div className="col-md-12 mb-2">
                                                    <strong>{tituloRango}</strong>
                                                </div>
                                            </div>
                                            <div className="row">
                                                <div className="col-md-3 mb-2 mb-md-0">
                                                    <label className="mb-1">Fecha inicio</label>
                                                    <input type="date" className="form-control" value={fechaInicioInput} onChange={(e) => setFechaInicioInput(e.target.value)} />
                                                </div>
                                                <div className="col-md-3 mb-2 mb-md-0">
                                                    <label className="mb-1">Fecha fin</label>
                                                    <input type="date" className="form-control" value={fechaFinInput} onChange={(e) => setFechaFinInput(e.target.value)} />
                                                </div>
                                                <div className="col-md-6 d-flex align-items-end">
                                                    <button
                                                        className="btn btn-info mr-2"
                                                        onClick={() => {
                                                            setFechaInicio(fechaInicioInput);
                                                            setFechaFin(fechaFinInput);
                                                        }}
                                                    >
                                                        <i className="fas fa-filter mr-1"></i>Filtrar
                                                    </button>
                                                    <button
                                                        className="btn btn-outline-secondary mr-2"
                                                        onClick={() => {
                                                            setFechaInicio('');
                                                            setFechaFin('');
                                                            setFechaInicioInput('');
                                                            setFechaFinInput('');
                                                        }}
                                                    >
                                                        Limpiar
                                                    </button>
                                                    <button className="btn btn-danger" onClick={exportarPdf}>
                                                        <i className="fas fa-file-pdf mr-1"></i>Descargar
                                                    </button>
                                                </div>
                                            </div>
                                        </div>

                                        {loadingDetalle && <p className="text-muted mb-0">Cargando estado de cuenta...</p>}

                                        {!loadingDetalle && (
                                            <>
                                                <div className="card" style={{ border: '1px solid #e9ecef' }}>
                                                    <div className="ec-section-title">PRESUPUESTOS ACTIVOS</div>
                                                    <div className="card-body p-0">
                                                        <table className="table table-sm mb-0 ec-table ec-table-presupuestos">
                                                            <colgroup>
                                                                <col />
                                                                <col />
                                                                <col />
                                                                <col />
                                                                <col />
                                                            </colgroup>
                                                            <thead>
                                                                <tr>
                                                                    <th className="text-center">N° Presupuesto</th>
                                                                    <th className="text-center">Fecha</th>
                                                                    <th className="text-center">Pagado</th>
                                                                    <th className="text-center">Total</th>
                                                                    <th className="text-center">Pendiente</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {presupuestosFiltrados.length === 0 && (
                                                                    <tr><td colSpan="5" className="text-center text-muted">No hay presupuestos (activos o pagados) para el rango seleccionado.</td></tr>
                                                                )}
                                                                {presupuestosFiltrados.map((pres) => {
                                                                    const total = parseFloat(pres.valor_total || 0);
                                                                    const pagado = parseFloat(pres.valor_consumido || 0);
                                                                    const pendiente = parseFloat(pres.saldo_disponible || 0);
                                                                    const numeroPres = pres.numero_presupuesto || `#${pres.id}`;
                                                                    const facturasAsociadas = getFacturasDePresupuesto(pres.id);
                                                                    return (
                                                                        <React.Fragment key={pres.id}>
                                                                            <tr>
                                                                                <td className="text-center">
                                                                                    <strong>{numeroPres}</strong>
                                                                                    {facturasAsociadas.length > 0 && (
                                                                                        <span className="text-muted ml-1" style={{ fontSize: '0.72rem' }}>
                                                                                            ({facturasAsociadas.length})
                                                                                        </span>
                                                                                    )}
                                                                                </td>
                                                                                <td className="text-center">{formatearFecha(pres.fecha_creacion)}</td>
                                                                                <td className="text-center">{formatearMoneda(pagado)}</td>
                                                                                <td className="text-center">{formatearMoneda(total)}</td>
                                                                                <td className="text-center">{formatearMoneda(pendiente)}</td>
                                                                            </tr>
                                                                            <tr className="presupuesto-facturas-row">
                                                                                <td colSpan="5">
                                                                                    <div className="presupuesto-facturas-panel">
                                                                                        <div className="presupuesto-facturas-panel-title">
                                                                                            Facturas asociadas a {numeroPres}
                                                                                        </div>
                                                                                        {facturasAsociadas.length === 0 ? (
                                                                                            <div className="text-muted" style={{ fontSize: '12px' }}>Sin facturas asociadas</div>
                                                                                        ) : (
                                                                                            <table className="presupuesto-facturas-table">
                                                                                                <thead>
                                                                                                    <tr>
                                                                                                        <th>N° Factura</th>
                                                                                                        <th>Fecha</th>
                                                                                                        <th>Concepto</th>
                                                                                                        <th>Forma de pago</th>
                                                                                                        <th>Tipo</th>
                                                                                                        <th>Total</th>
                                                                                                        <th>Estado</th>
                                                                                                    </tr>
                                                                                                </thead>
                                                                                                <tbody>
                                                                                                    {facturasAsociadas.map((factura) => (
                                                                                                        <tr
                                                                                                            key={`pres-fact-${factura.id}`}
                                                                                                            className={esFacturaAnulada(factura) ? 'fila-anulado' : ''}
                                                                                                        >
                                                                                                            <td><strong>{getNumeroFactura(factura)}</strong></td>
                                                                                                            <td>{formatearFecha(factura.fecha_emision)}</td>
                                                                                                            <td className="concepto-cell" title={factura.concepto_factura_nombre || ''}>
                                                                                                                {factura.concepto_factura_nombre || '—'}
                                                                                                            </td>
                                                                                                            <td>
                                                                                                                {factura.forma_pago && factura.forma_pago !== 'PENDIENTE'
                                                                                                                    ? getFormaPagoTexto(factura.forma_pago)
                                                                                                                    : '—'}
                                                                                                            </td>
                                                                                                            <td>{getTipoFacturaTexto(factura)}</td>
                                                                                                            <td>{formatearMoneda(factura.valor_total)}</td>
                                                                                                            <td>
                                                                                                                <span
                                                                                                                    className="px-2 py-1"
                                                                                                                    style={{
                                                                                                                        ...getEstadoFacturaBadgeStyle(factura.estado),
                                                                                                                        display: 'inline-block',
                                                                                                                        fontSize: '11px',
                                                                                                                        fontWeight: 600,
                                                                                                                    }}
                                                                                                                >
                                                                                                                    {getEstadoFacturaTexto(factura.estado)}
                                                                                                                </span>
                                                                                                            </td>
                                                                                                        </tr>
                                                                                                    ))}
                                                                                                </tbody>
                                                                                            </table>
                                                                                        )}
                                                                                    </div>
                                                                                </td>
                                                                            </tr>
                                                                        </React.Fragment>
                                                                    );
                                                                })}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>

                                                <div className="card" style={{ border: '1px solid #e9ecef' }}>
                                                    <div className="ec-section-title">LISTADO GENERAL DE FACTURAS</div>
                                                    <div className="card-body p-0">
                                                        <table className="table table-sm mb-0 ec-table">
                                                            <thead>
                                                                <tr>
                                                                    <th className="text-center">N° Factura</th>
                                                                    <th className="text-center">Fecha</th>
                                                                    <th className="text-center">Concepto</th>
                                                                    <th className="text-center">Forma de pago</th>
                                                                    <th className="text-center">Tipo</th>
                                                                    <th className="text-center">Total</th>
                                                                    <th className="text-center">Estado</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {facturasFiltradas.length === 0 && (
                                                                    <tr><td colSpan="7" className="text-center text-muted">No hay facturas para el rango seleccionado.</td></tr>
                                                                )}
                                                                {facturasFiltradas.map((factura) => (
                                                                        <tr
                                                                            key={factura.id}
                                                                            className={esFacturaAnulada(factura) ? 'fila-anulado' : ''}
                                                                        >
                                                                            <td className="text-center">{getNumeroFactura(factura)}</td>
                                                                            <td className="text-center">{formatearFecha(factura.fecha_emision)}</td>
                                                                            <td className="text-center concepto-cell" title={factura.concepto_factura_nombre || ''}>
                                                                                {factura.concepto_factura_nombre || '—'}
                                                                            </td>
                                                                            <td className="text-center">
                                                                                {factura.forma_pago && factura.forma_pago !== 'PENDIENTE'
                                                                                    ? getFormaPagoTexto(factura.forma_pago)
                                                                                    : '—'}
                                                                            </td>
                                                                            <td className="text-center">{getTipoFacturaTexto(factura)}</td>
                                                                            <td className="text-center">{formatearMoneda(factura.valor_total)}</td>
                                                                            <td className="text-center">
                                                                                <span
                                                                                    className="px-2 py-1"
                                                                                    style={{
                                                                                        ...getEstadoFacturaBadgeStyle(factura.estado),
                                                                                        display: 'inline-block',
                                                                                        fontSize: '12px',
                                                                                        fontWeight: 600,
                                                                                    }}
                                                                                >
                                                                                    {getEstadoFacturaTexto(factura.estado)}
                                                                                </span>
                                                                            </td>
                                                                        </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </section>
            </div>

            <Footer showToast={false} delay={0} />
        </div>
    );
}

export default EstadoCuentaReportes;
